'use client';

import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { useCallback, useRef, useState } from 'react';

import type { RouterInputs } from '@poseidon/api/types';
import type { SoftphoneClient, SoftphoneProvider } from '@poseidon/components/Softphone/softphoneClient.types';
import { CALL_OUTCOMES, WRONG_NUMBER_REMOVED_TOAST } from '@poseidon/components/Softphone/CallOutcomePicker';
import { ClickToCallButtonPure } from '@poseidon/components/Softphone/ClickToCallButtonPure';
import { createSoftphoneClient } from '@poseidon/components/Softphone/createSoftphoneClient';
import { useSoftphone } from '@poseidon/components/Softphone/useSoftphone';

import { api } from '@/utils/api';

type CallOutcome = RouterInputs['voiceCall']['logOutcome']['outcome'];

function isCallOutcome(value: string): value is CallOutcome {
  return CALL_OUTCOMES.some((o) => o.value === value);
}

export interface ClickToCallButtonProps {
  teamId: string;
  /** Public contact id (contact_public_id). */
  contactId: string;
  contactName?: string;
  phoneNumber?: string | null;
  /** Team feature flag — when false the affordance is hidden entirely. */
  enabled: boolean;
  /**
   * Whether the rep has a caller number provisioned. When false (and `enabled` is true), the
   * button renders greyed/disabled with a tooltip instead of disappearing.
   */
  callingConnected: boolean;
  /** The team's carrier; picks the matching softphone client (Telnyx vs Twilio). */
  provider: SoftphoneProvider;
  /**
   * When placing the call for a cadence `call` step, the cadence_step id. Threaded to
   * `startCall` so logging the outcome completes that step server-side.
   */
  cadenceStepId?: string;
  /** Called after a call outcome is logged (e.g. to invalidate the contact / reminder caches). */
  onCallLogged?: () => void;
  className?: string;
}

/**
 * Dashboard click-to-call entry point. Wires the tRPC voice routes to the shared, provider-
 * agnostic `ClickToCallButtonPure`. The carrier is chosen server-side and passed in as
 * `provider`, so this button is the same for Telnyx and Twilio — only the client differs.
 */
export function ClickToCallButton({
  teamId,
  contactId,
  contactName,
  phoneNumber,
  enabled,
  callingConnected,
  provider,
  cadenceStepId,
  onCallLogged,
  className,
}: ClickToCallButtonProps) {
  const [open, setOpen] = useState(false);
  const [changeNumberOpen, setChangeNumberOpen] = useState(false);

  const utils = api.useUtils();
  const getTokenMutation = api.voiceCall.getClientToken.useMutation();
  const startCallMutation = api.voiceCall.startCall.useMutation();
  const logOutcomeMutation = api.voiceCall.logOutcome.useMutation();
  const setDialNumberMutation = api.voiceCall.setContactDialNumber.useMutation();
  // The contact's pinned from-number + the rep's bucket, for the dialer's "Calling from" line
  // and the guarded change-number flow. Only fetched while the dialer is open.
  const dialInfoQuery = api.voiceCall.getContactDialInfo.useQuery(
    { teamId, contactId },
    { enabled: open && enabled && callingConnected },
  );

  // One client per mounted button (matching the team's carrier); useSoftphone owns its
  // connect/teardown lifecycle.
  const clientRef = useRef<SoftphoneClient | null>(null);
  if (clientRef.current === null) clientRef.current = createSoftphoneClient(provider);
  const client = clientRef.current;

  const fetchToken = useCallback(async () => {
    const res = await getTokenMutation.mutateAsync({ teamId });
    return { token: res.token };
  }, [getTokenMutation, teamId]);

  const startServerCall = useCallback(async () => {
    const res = await startCallMutation.mutateAsync({ teamId, contactId, stepId: cadenceStepId });
    // A first dial pins the contact's from-number server-side — refresh the dial info so the
    // change-number flow sees the new association.
    void utils.voiceCall.getContactDialInfo.invalidate({ teamId, contactId });
    return {
      voiceCallId: res.voiceCallId,
      destinationNumber: res.destinationNumber,
      callerNumber: res.callerNumber,
      recordingEnabled: res.recordingEnabled,
    };
  }, [startCallMutation, teamId, contactId, cadenceStepId, utils]);

  const controller = useSoftphone({ client, fetchToken, startServerCall });
  // Destructure the pieces the outcome handler closes over so its dep array lists stable
  // identifiers instead of the whole `controller` (a new object every render).
  const { voiceCallId, close: closeDialer } = controller;

  const handleCall = useCallback(() => {
    setOpen(true);
    void controller.start();
  }, [controller]);

  const handleClose = useCallback(() => {
    controller.close();
    setOpen(false);
    setChangeNumberOpen(false);
  }, [controller]);

  const handleConfirmChangeNumber = useCallback(
    async (newNumber: string) => {
      await updateWithToast({
        loading: 'Updating calling number…',
        success: 'Calling number updated',
        mutationFnAsync: () => setDialNumberMutation.mutateAsync({ teamId, contactId, phoneNumber: newNumber }),
      });
      void utils.voiceCall.getContactDialInfo.invalidate({ teamId, contactId });
      setChangeNumberOpen(false);
    },
    [setDialNumberMutation, teamId, contactId, utils],
  );

  const bucketNumbers = dialInfoQuery.data?.bucketNumbers ?? [];
  // The in-flight call's number is authoritative (a first dial pins it server-side); before a
  // call starts, fall back to the already-pinned number from the query.
  const callerNumber = controller.callerNumber ?? dialInfoQuery.data?.stickyNumber ?? null;
  // An empty bucket means a legacy/superadmin-provisioned rep, not a stale association.
  const callerNumberStale = callerNumber != null && bucketNumbers.length > 0 && !bucketNumbers.includes(callerNumber);

  const handleSelectOutcome = useCallback(
    async (outcome: string) => {
      if (voiceCallId && isCallOutcome(outcome)) {
        await updateWithToast({
          loading: 'Saving call outcome…',
          // A "wrong number" outcome also removes the dialed number from the contact server-side.
          success: outcome === 'wrong_number' ? WRONG_NUMBER_REMOVED_TOAST : 'Call logged',
          mutationFnAsync: () => logOutcomeMutation.mutateAsync({ teamId, voiceCallId, outcome }),
        });
        // Refresh the contact's conversation thread so the just-logged call (and its
        // disposition) appears without a manual reload. Done here so BOTH the profile and
        // cadence call buttons get it, regardless of what their `onCallLogged` does.
        void utils.history.contact.invalidate({ contactId });
        onCallLogged?.();
      }
      // Hang up any still-live call and reset the controller before unmounting the dialer, so the
      // next call to this contact starts clean rather than inheriting this call's state.
      closeDialer();
      setOpen(false);
    },
    [voiceCallId, closeDialer, logOutcomeMutation, teamId, contactId, utils, onCallLogged],
  );

  return (
    <ClickToCallButtonPure
      enabled={enabled}
      callingConnected={callingConnected}
      phoneNumber={phoneNumber}
      contactName={contactName}
      open={open}
      onCall={handleCall}
      onClose={handleClose}
      status={controller.status}
      durationSec={controller.durationSec}
      isMuted={controller.isMuted}
      error={controller.error}
      isRecording={controller.isRecording}
      outcomes={CALL_OUTCOMES}
      onToggleMute={controller.toggleMute}
      onHangup={controller.hangup}
      onSelectOutcome={(outcome) => void handleSelectOutcome(outcome)}
      callerNumber={callerNumber}
      callerNumberStale={callerNumberStale}
      bucketNumbers={bucketNumbers}
      changeNumberOpen={changeNumberOpen}
      changeNumberSaving={setDialNumberMutation.isPending}
      onOpenChangeNumber={() => setChangeNumberOpen(true)}
      onCloseChangeNumber={() => setChangeNumberOpen(false)}
      onConfirmChangeNumber={(newNumber) => void handleConfirmChangeNumber(newNumber)}
      className={className}
    />
  );
}
